import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fileUploadService } from '@/services/file-upload.service'
import { type ProductDTO, productService } from '@/services/product.service'
import { previewRetry, previewRetryDelay } from './preview-retry'
import { checkImageFile, uploadProductImage } from './product-image'
import { productKey } from './useProductEditor'
import { IMAGE_URL_STALE_MS } from './useProductList'

/**
 * The presigned preview URL of the product image (only with `file.read`). A
 * just-uploaded image answers 409 until its malware scan is clean (TO-458), so
 * that one failure is retried every few seconds.
 */
function usePreviewUrl(fileId: string | null, enabled: boolean) {
	return useQuery({
		queryKey: ['file-url', fileId],
		queryFn: () => fileUploadService.downloadUrl(fileId as string),
		enabled: enabled && fileId !== null,
		staleTime: IMAGE_URL_STALE_MS,
		retry: previewRetry,
		retryDelay: previewRetryDelay,
	})
}

/**
 * Image section of the product editor (TO-416 product-admin-ui REQ-005): the
 * pre-check, the four-step upload, remove, and the preview URL.
 */
export function useProductImage(product: ProductDTO, caps: { uploadImage: boolean; viewImages: boolean }) {
	const { t } = useTranslation('products')
	const client = useQueryClient()
	const [error, setError] = useState<string | null>(null)
	// Set synchronously before a mutation starts: `isPending` only turns true after a
	// render, so two selections in the same tick would otherwise both start.
	const inFlight = useRef(false)
	const settle = {
		onSettled: () => {
			inFlight.current = false
		},
	}
	const fileId = product.imageFileId
	const preview = usePreviewUrl(fileId, caps.viewImages)
	const attach = async (imageFileId: string | null) => {
		const updated = await productService.update(product.productId, { imageFileId })
		client.setQueryData(productKey(product.productId), updated)
	}
	const upload = useMutation({
		mutationFn: (file: File) =>
			uploadProductImage(file, product.productId, {
				requestUrl: fileUploadService.requestUrl,
				putBytes: fileUploadService.putBytes,
				confirm: fileUploadService.confirm,
				attach,
			}),
		onError: () => setError(t('image.errors.upload')),
	})
	const remove = useMutation({ mutationFn: () => attach(null), onError: () => setError(t('image.errors.upload')) })

	return {
		t,
		imageUrl: fileId && caps.viewImages ? (preview.data ?? null) : null,
		hasImage: fileId !== null,
		canEdit: caps.uploadImage,
		isBusy: upload.isPending || remove.isPending,
		error,
		onFileSelected: (file: File) => {
			if (inFlight.current) return
			const refusal = checkImageFile(file)
			setError(refusal ? t(refusal) : null)
			if (refusal) return
			inFlight.current = true
			upload.mutate(file, settle)
		},
		onRemove: () => {
			if (inFlight.current) return
			inFlight.current = true
			setError(null)
			remove.mutate(undefined, settle)
		},
	}
}
